import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Button, Badge, Alert, Form, Row, Col, Spinner, Card } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { FiEye, FiUsers, FiCheckCircle, FiArrowLeft } from 'react-icons/fi';
import { Link } from 'react-router-dom';

// Debounce hook
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

const TrainingList = () => {
    const [trainings, setTrainings] = useState([]);
    const [feedback, setFeedback] = useState({});
    const [loadingAction, setLoadingAction] = useState({});
    const [loadingData, setLoadingData] = useState(false);
    
    // URL Params state management
    const [searchParams, setSearchParams] = useSearchParams();
    const initialStatus = searchParams.get('status') || 'Pendente';
    const initialSearch = searchParams.get('search') || '';

    const [filters, setFilters] = useState({
        status: initialStatus,
        search: initialSearch
    });

    const debouncedSearch = useDebounce(filters.search, 300);
    
    const user = JSON.parse(localStorage.getItem('user'));
    const navigate = useNavigate();

    // Effect to fetch data when filters (debounced) change
    useEffect(() => {
        // Update URL params
        const params = {};
        if (filters.status) params.status = filters.status;
        if (debouncedSearch) params.search = debouncedSearch;
        setSearchParams(params);

        fetchTrainings(filters.status, debouncedSearch);
    }, [filters.status, debouncedSearch, setSearchParams]);

    const fetchTrainings = async (status, search) => {
        setLoadingData(true);
        try {
            const params = {};
            if (status) params.status = status;
            if (search) params.search = search;
            
            const res = await api.get('/trainings', { params });
            setTrainings(res.data);
        } catch (err) {
            console.error(err);
            setFeedback({ type: 'danger', text: 'Erro ao carregar treinamentos.' });
        } finally {
            setLoadingData(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleConclude = async (id) => {
        if (!window.confirm("Tem certeza que deseja concluir este treinamento?")) return;

        setLoadingAction(prev => ({ ...prev, [id]: true }));
        setFeedback({});
        
        try {
            await api.put(`/trainings/${id}/conclude`);
            
            // Update local state without reload
            setTrainings(prev => prev.map(t => 
                t.id === id ? { ...t, status: 'Concluído' } : t
            ));
            
            setFeedback({ type: 'success', text: 'Treinamento concluído com sucesso!' });
        } catch (err) {
            console.error(err);
            setFeedback({ type: 'danger', text: err.response?.data?.msg || 'Erro ao concluir treinamento.' });
        } finally {
            setLoadingAction(prev => ({ ...prev, [id]: false }));
        }
    };

    return (
        <Container className="mt-4">
            {feedback.text && <Alert variant={feedback.type} onClose={() => setFeedback({})} dismissible>{feedback.text}</Alert>}
            
            <div className="mb-4">
                <Button 
                    variant="link" 
                    as={Link} 
                    to="/dashboard" 
                    className="text-decoration-none p-0 mb-2 text-muted"
                >
                    <FiArrowLeft className="me-1" /> Voltar para Dashboard
                </Button>                
            </div>
            <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-4 fw-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#006064' }}>
                    Lista de treinamento
                </h2>
                {user.perfil !== 'Participante' && (
                    <Button variant="primary" onClick={() => navigate('/create-training')}>
                        Novo Treinamento
                    </Button>
                )}
            </div>            

            {/* Filters Section */}
            <div className="bg-light p-3 rounded mb-4 shadow-sm">
                <Row className="g-3 align-items-end">
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Buscar por Nome</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Digite para buscar..."
                                name="search"
                                value={filters.search}
                                onChange={handleFilterChange}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Filtrar por Status</Form.Label>
                            <Form.Select 
                                name="status" 
                                value={filters.status} 
                                onChange={handleFilterChange}
                            >
                                <option value="Todos">Todos</option>
                                <option value="Pendente">Pendente</option>
                                <option value="Em andamento">Em andamento</option>
                                <option value="Concluído">Concluído</option>
                                <option value="Cancelado">Cancelado</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={4} className="text-end">
                         <span className="text-muted fw-bold">
                             {loadingData ? (
                                 <><Spinner animation="border" size="sm" className="me-2" />Carregando...</>
                             ) : (
                                 <>{trainings.length} resultados encontrados</>
                             )}
                         </span>
                    </Col>
                </Row>
            </div>

            {loadingData ? (
                <div className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </Spinner>
                </div>
            ) : (
                <Card className="shadow-sm border-0">
                    <Card.Body className="p-0">
                        <Table hover responsive className="mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0 text-muted small fw-bold text-uppercase ps-4 py-3">Título</th>
                                    <th className="border-0 text-muted small fw-bold text-uppercase py-3">Data</th>
                                    <th className="border-0 text-muted small fw-bold text-uppercase py-3">Local</th>
                                    <th className="border-0 text-muted small fw-bold text-uppercase py-3">Status</th>
                                    <th className="border-0 text-muted small fw-bold text-uppercase text-end pe-4 py-3">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trainings.length > 0 ? (
                                    trainings.map(t => (
                                        <tr key={t.id} className="align-middle border-bottom">
                                            <td className="ps-4 py-3 fw-bold text-dark">{t.titulo}</td>
                                            <td className="py-3 text-muted">{new Date(t.data).toLocaleString()}</td>
                                            <td className="py-3 text-muted">{t.local || '-'}</td>
                                            <td className="py-3">
                                                <Badge bg={t.status === 'Concluído' ? 'success' : 'warning'} className="fw-normal px-3 py-2 rounded-pill">
                                                    {t.status}
                                                </Badge>
                                            </td>
                                            <td className="text-end pe-4 py-3">
                                                <div className="d-flex justify-content-end gap-2">
                                                    <Button size="sm" variant="outline-info" className="d-inline-flex align-items-center gap-1" onClick={() => navigate(`/training/${t.id}`)}>
                                                        <FiEye /> Detalhes
                                                    </Button>
                                                    <Button size="sm" variant="outline-primary" className="d-inline-flex align-items-center gap-1" onClick={() => navigate(`/training/${t.id}/participants`)}>
                                                        <FiUsers /> Participantes
                                                    </Button>
                                                    {user.perfil !== 'Participante' && (
                                                        <>
                                                            <Button 
                                                                size="sm" 
                                                                variant={t.status === 'Concluído' ? "outline-success" : "success"}
                                                                disabled={t.status === 'Concluído' || loadingAction[t.id]}
                                                                onClick={() => handleConclude(t.id)}
                                                                className="d-inline-flex align-items-center gap-1"
                                                                title={t.status === 'Concluído' ? "Treinamento já concluído" : "Concluir treinamento"}
                                                            >
                                                                {loadingAction[t.id] ? <Spinner size="sm" animation="border" /> : (t.status === 'Concluído' ? <><FiCheckCircle /> Concluído</> : <><FiCheckCircle /> Concluir</>)}
                                                            </Button>
                                                            <Button size="sm" variant="outline-secondary" className="d-inline-flex align-items-center gap-1" onClick={() => navigate(`/report/${t.id}`)}>
                                                                Relatório
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-5 text-muted">
                                            Nenhum treinamento encontrado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
};

export default TrainingList;